import { AxiosError } from 'axios';
import {
  filter,
  find,
  isEmpty,
  isNil,
  keyBy,
  map,
  omitBy,
  reduce,
} from 'lodash';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import {
  authApi,
  banksApi,
  currencyApi,
  orderRequisitesApi,
  ordersApi,
  tradeMethodsApi,
} from 'api';
import {
  Layout,
  PaymentCard,
  PaymentDetails,
  PaymentLayoutAction,
  PaymentLoading,
  PaymentRules,
  PaymentStatus,
  UploadReceiptDialog,
  Loader,
  useSciDomain,
  Button,
} from 'components';
import { OrderContext, OrderContextProps } from 'context';
import {
  Bank,
  CancelStatusDetails,
  CustomerConfirmStatusDetails,
  CustomerDataCollectionOrder,
  EventName,
  EventOptionClickAction,
  FormFieldType,
  NewStatusDetails,
  OrderCallbackUrlStatus,
  OrderStatus,
  PaymentType,
  QueryKey,
  StatusCode,
} from 'enums';
import { TranslationNamespace } from 'i18n';
import { NotFoundPage } from 'pages/NotFound';
import { Order, PaymentFormValues, TradeMethod, UpdateOrderDto } from 'types';
import { authUtils, gaUtils } from 'utils';

const COMPLETED_ORDER_STATUSES = [OrderStatus.Cancelled, OrderStatus.Completed];

const CUSTOMER_FIELDS: Array<keyof PaymentFormValues> = [
  'customerCardFirstDigits',
  'customerCardLastDigits',
  'customerName',
  'customerBank',
  'customerPhoneLastDigits',
  'customerUtr',
  'customerIBAN',
  'customerAccountNumber',
];

export const OrderPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'pages.order',
  });
  const { t: tUploadReceipt } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.upload_confirmation_receipt',
  });

  const params = useParams<{ id: string; token: string }>();
  const authData = useMemo(
    () => ({ orderId: params.id as string, token: params.token as string }),
    [params],
  );
  const { orderId } = useMemo(() => authData, [authData]);
  const [timeoutRefetch, setTimeoutRefetch] = useState(false);
  const [paymentFormValues, setPaymentFormValues] = useState<PaymentFormValues>(
    {
      customerCardFirstDigits: null,
      customerCardLastDigits: null,
      customerName: null,
      customerBank: null,
      customerPhoneLastDigits: null,
      customerUtr: null,
      customerIBAN: null,
      customerAccountNumber: null,
    },
  );
  const [order, setOrder] = useState<Order>();
  const [initialOrderFetched, setInitialOrderFetched] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [authenticationFailed, setAuthenticationFailed] = useState(false);
  const [selectedTradeMethod, setSelectedTradeMethod] = useState<TradeMethod>();
  const [
    enableSelectedTradeMethodFallback,
    setEnableSelectedTradeMethodFallback,
  ] = useState(false);

  const status = useMemo(() => order?.status as OrderStatus, [order]);

  const isCallbackUrlStatusInProgress = useMemo(
    () =>
      order?.integration?.callbackUrlStatus ===
      OrderCallbackUrlStatus.InProgress,
    [order],
  );

  const shouldRefetch = useMemo(() => {
    if ([OrderStatus.Requisites].includes(status)) {
      return !order?.requisites;
    } else if (
      [
        OrderStatus.CustomerConfirm,
        OrderStatus.TraderConfirm,
        OrderStatus.HoldCompleted,
        OrderStatus.Dispute,
      ].includes(status)
    ) {
      return true;
    } else if (COMPLETED_ORDER_STATUSES.includes(status)) {
      return isCallbackUrlStatusInProgress;
    }
    return false;
  }, [isCallbackUrlStatusInProgress, order?.requisites, status]);

  const { isLoading: isAuthLoading } = useQuery(
    [QueryKey.Login, authData],
    () => authApi.login(authData),
    {
      enabled: !authenticated && !authenticationFailed,
      retry: false,
      onSuccess: (response: { accessToken: string }) => {
        authUtils.setAuthData(authData);
        authUtils.setAccessToken(response?.accessToken);
        setAuthenticated(true);
      },
      onError: () => {
        setAuthenticationFailed(true);
      },
    },
  );

  const handleError = useCallback((error: any) => {
    setAuthenticationFailed(true);
  }, []);

  const { isLoading: isOrderLoading } = useQuery(
    [QueryKey.Order],
    () => ordersApi.getOne(orderId),
    {
      enabled: authenticated && !initialOrderFetched,
      onSuccess: setOrder,
      onError: handleError,
      onSettled: () => {
        setInitialOrderFetched(true);
      },
    },
  );

  const { isLoading: isBanksLoading, data: banks } = useQuery(
    QueryKey.Banks,
    banksApi.getAll,
    { enabled: !!order, staleTime: Infinity },
  );
  const { isLoading: isCurrenciesLoading, data: currencies } = useQuery(
    QueryKey.Currencies,
    currencyApi.getFiat,
    { enabled: !!order, staleTime: Infinity },
  );

  const { data: sciDomainConfig } = useSciDomain();

  const shouldLoadTradeMethods = useMemo(
    () =>
      authenticated && order?.status === OrderStatus.New && !!sciDomainConfig,
    [authenticated, order?.status, sciDomainConfig],
  );

  const { isLoading: isFakeTradeMethodsLoading, data: fakeTradeMethods } =
    useQuery<TradeMethod[]>(
      QueryKey.FakeTradeMethods,
      () => tradeMethodsApi.getShopSCITradeMethods((order as Order)?.id),
      {
        enabled:
          shouldLoadTradeMethods && sciDomainConfig?.shopSCITradeMethodsOnly,
        staleTime: Infinity,
      },
    );

  const { isLoading: isTradeMethodsLoading, data: tradeMethods } = useQuery<
    TradeMethod[]
  >(QueryKey.TradeMethods, () => tradeMethodsApi.getAll((order as Order)?.id), {
    enabled:
      shouldLoadTradeMethods && !sciDomainConfig?.shopSCITradeMethodsOnly,
    staleTime: Infinity,
  });

  const apiActionOptions = useMemo(
    () => ({
      onSuccess: (data: Order) => {
        // update order if it was initialized already
        if (!!order && !isOrderLoading) {
          setOrder(data);
        }
        const updatedValues = reduce(
          CUSTOMER_FIELDS,
          (result, field) => {
            result[field] = paymentFormValues[field] || data?.payment?.[field];
            return result;
          },
          {} as Partial<PaymentFormValues>,
        );
        setPaymentFormValues(updatedValues);
      },
      // try to refresh order as it can be moved to another status e.g. dispute
      onError: (error: AxiosError) => {
        if (error?.response?.status === StatusCode.Unauthorized) {
          setAuthenticationFailed(true);
        } else {
          setInitialOrderFetched(false);
        }
      },
    }),
    [paymentFormValues, order, isOrderLoading],
  );

  const queryResultOrder = useQuery(
    QueryKey.Order,
    () => ordersApi.getOne(orderId),
    {
      enabled: shouldRefetch,
      refetchInterval: 3000,
      ...apiActionOptions,
    },
  );

  useQuery(QueryKey.Order, () => ordersApi.getOne(orderId), {
    enabled: timeoutRefetch,
    ...apiActionOptions,
  });

  const { data: paymentFields, isLoading: paymentFieldsLoading } = useQuery(
    QueryKey.OrderPaymentFields,
    () => ordersApi.getPaymentFields(orderId),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: order?.status === OrderStatus.CustomerConfirm,
    },
  );

  const isNSPK = useMemo(
    () => selectedTradeMethod?.paymentType === PaymentType.NSPK,
    [selectedTradeMethod],
  );

  const { data: nspkDetails } = useQuery(
    QueryKey.NspkDetails,
    () => orderRequisitesApi.getNspkDetails(order?.id || ''),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: isNSPK && order?.status === OrderStatus.CustomerConfirm,
    },
  );

  const nspkUrl = useMemo(() => {
    if (isNSPK) {
      return nspkDetails?.qrLink;
    }
  }, [isNSPK, nspkDetails]);

  const qrCodeUrl = useMemo(() => {
    if (order?.requisites?.qrImageUrl) {
      return order.requisites.qrImageUrl;
    }
    return nspkDetails?.qrImage;
  }, [order, nspkDetails]);

  const [startPaymentSyncFailed, setStartPaymentSyncFailed] = useState(false);

  const { mutate: update } = useMutation(ordersApi.update, apiActionOptions);
  const {
    mutate: startPaymentSync,
    isLoading: isStartPaymentSyncAPICallInProgress,
  } = useMutation(ordersApi.startPaymentSync, apiActionOptions);

  const { mutate: startPayment } = useMutation(
    ordersApi.startPayment,
    apiActionOptions,
  );
  const { mutate: confirmPayment } = useMutation(
    ordersApi.confirmPayment,
    apiActionOptions,
  );

  const { mutate: cancel } = useMutation(ordersApi.cancel, apiActionOptions);

  const updatePaymentInfo = useCallback(
    (
      updateDto: UpdateOrderDto,
      {
        onSuccess,
        onError,
      }: { onSuccess?: () => void; onError?: () => void } = {},
    ) => {
      update(
        { id: orderId, data: updateDto },
        {
          onSuccess: (data) => {
            apiActionOptions.onSuccess(data);
            onSuccess?.();
          },
          onError: (data) => {
            apiActionOptions.onError(data);
            onError?.();
          },
        },
      );
    },
    [orderId, update, apiActionOptions],
  );

  const returnUrl = useMemo(
    () => order?.integration?.returnUrl,
    [order?.integration?.returnUrl],
  );

  const successUrl = useMemo(
    () => order?.integration?.successUrl || order?.integration?.returnUrl,
    [order?.integration],
  );

  const failUrl = useMemo(
    () => order?.integration?.failUrl || order?.integration?.returnUrl,
    [order?.integration],
  );

  const navigateReturnUrl = useCallback(() => {
    const url =
      status === OrderStatus.Completed
        ? successUrl
        : status === OrderStatus.Cancelled
        ? failUrl
        : returnUrl;
    if (url) {
      window.location.href = url;
    }
  }, [status, returnUrl, successUrl, failUrl]);

  const selectPaymentType = useCallback(
    (tradeMethod: TradeMethod) => {
      if (sciDomainConfig?.shopSCITradeMethodsOnly) {
        startPaymentSync(
          {
            id: orderId,
            data: {
              payment: {
                type: tradeMethod.paymentType,
                bank: tradeMethod.bank,
              },
            },
          },
          {
            onSuccess: (order: Order) => {
              if (!order.requisites) {
                setStartPaymentSyncFailed(true);
              }
            },
            onError: () => {
              setStartPaymentSyncFailed(true);
            },
          },
        );
      } else {
        updatePaymentInfo(
          {
            payment: {
              type: tradeMethod.paymentType,
              bank: tradeMethod.bank,
            },
          },
          { onSuccess: () => startPayment(orderId) },
        );
      }
    },
    [
      orderId,
      startPayment,
      updatePaymentInfo,
      startPaymentSync,
      sciDomainConfig,
    ],
  );

  const isStartPaymentSyncInProgress = useMemo(
    () =>
      isStartPaymentSyncAPICallInProgress ||
      order?.statusDetails === NewStatusDetails.NewStartPaymentSyncAttempt,
    [isStartPaymentSyncAPICallInProgress, order?.statusDetails],
  );

  const { data: selectedTradeMethodsFallback } = useQuery<TradeMethod>(
    QueryKey.SelectedTradeMethodsFallback,
    () => tradeMethodsApi.getSelectedTradeMethod((order as Order)?.id),
    {
      enabled: !!order && enableSelectedTradeMethodFallback && authenticated,
      staleTime: Infinity,
    },
  );

  const canManageReceipts = useMemo(
    () =>
      (order?.status === OrderStatus.Cancelled &&
        ![
          CancelStatusDetails.CancelledRequisitesTimeout,
          CancelStatusDetails.CancelledSyncRequisitesAttempts,
        ].includes(order?.statusDetails as CancelStatusDetails)) ||
      order?.status === OrderStatus.Dispute,
    [order],
  );

  const { data: receipts, isLoading: isReceiptsLoading } = useQuery(
    QueryKey.OrderReceipts,
    () => ordersApi.getOrderReceipts(orderId),
    {
      enabled: canManageReceipts,
      retry: false,
      onError: apiActionOptions.onError,
    },
  );

  useEffect(() => {
    if (order?.payment?.type && order?.status !== OrderStatus.New) {
      const bank = order?.requisites?.bank || order?.payment?.bank;
      const selectedTradeMethodsLoaded = find(
        tradeMethods,
        (tradeMethod) =>
          tradeMethod.paymentType === order?.payment?.type &&
          (!bank || tradeMethod.bank === bank),
      );
      const tradeMethod =
        selectedTradeMethodsLoaded || selectedTradeMethodsFallback;

      if (tradeMethod) {
        setSelectedTradeMethod(tradeMethod);
      } else if (!enableSelectedTradeMethodFallback) {
        setEnableSelectedTradeMethodFallback(true);
      }
    }
  }, [
    order,
    order?.payment?.type,
    order?.payment?.bank,
    order?.status,
    tradeMethods,
    setSelectedTradeMethod,
    selectedTradeMethodsFallback,
    enableSelectedTradeMethodFallback,
  ]);

  const isAmobile = useMemo(
    () =>
      order?.requisites?.bank === Bank.Amobile ||
      order?.requisites?.bank === Bank.AmobileAlternate,
    [order?.requisites?.bank],
  );

  const customerFields = useMemo(() => {
    if (!selectedTradeMethod) {
      return [];
    }

    return filter(
      map(selectedTradeMethod.customerFields, (field) => {
        const override = find(paymentFields, { name: field.name });
        return {
          ...field,
          ...override,
        };
      }),
      (field) =>
        !field.hidden ||
        // SPAY-3094, SPAY-3195 hardcode
        (field.name === 'customerBank' &&
          order?.payment?.type === PaymentType.SBP &&
          isAmobile),
    );
  }, [paymentFields, selectedTradeMethod, isAmobile, order?.payment?.type]);

  const requiredCustomerFields = useMemo(() => {
    if (isEmpty(customerFields)) {
      return [];
    }
    return customerFields
      .filter((field) => field.required)
      .map((field) => field.name) as Array<keyof PaymentFormValues>;
  }, [customerFields]);

  const isCustomerDataConfirmed = useMemo(() => {
    if (
      isAmobile &&
      order?.payment?.type === PaymentType.SBP &&
      !order?.payment?.customerBank
    ) {
      // SPAY-3094, SPAY-3195 hardcode
      return false;
    }
    if (!order?.payment?.type && !selectedTradeMethod) {
      return false;
    }
    return requiredCustomerFields.every((field) => order?.payment?.[field]);
  }, [order?.payment, isAmobile, requiredCustomerFields, selectedTradeMethod]);

  const isCardInfoConfirmDisabled = useMemo(() => {
    if (!selectedTradeMethod) {
      return false;
    }
    return requiredCustomerFields.some((field) => !paymentFormValues[field]);
  }, [requiredCustomerFields, paymentFormValues, selectedTradeMethod]);

  const customerDataBeforePayment = useMemo(
    () =>
      order?.shop.customerDataCollectionOrder ===
      CustomerDataCollectionOrder.BeforePayment,
    [order?.shop.customerDataCollectionOrder],
  );

  const refetchOrder = useCallback(
    // Order may be in 'cancelled' status -> query may be `enabled: false` -> invalidate won't work -> manually trigger refetch
    () => queryResultOrder.refetch(),
    [queryResultOrder],
  );

  const [uploadReceiptModalOpen, setUploadReceiptModalOpen] = useState(false);

  const uploadModalLocaleOverride = useMemo(
    () => ({
      dropPasteImportFiles: tUploadReceipt('description'),
      uploadXFiles: {
        0: tUploadReceipt('submit'),
      },
    }),
    [tUploadReceipt],
  );

  const confirmCard = useCallback(
    (formValues: PaymentFormValues) => {
      gaUtils.trackEvent(EventName.Click, {
        action: EventOptionClickAction.ConfirmCustomerDetails,
      });
      updatePaymentInfo(
        {
          payment: omitBy(formValues, isNil),
        },
        customerDataBeforePayment
          ? {}
          : {
              onSuccess: () => {
                if (order?.shop?.collectCustomerReceipts) {
                  setUploadReceiptModalOpen(true);
                } else {
                  confirmPayment(orderId);
                }
              },
            },
      );
    },
    [
      confirmPayment,
      customerDataBeforePayment,
      orderId,
      updatePaymentInfo,
      order?.shop?.collectCustomerReceipts,
    ],
  );

  const actions: Record<string, PaymentLayoutAction> = useMemo(
    () => ({
      cancel: {
        label: t('buttons.cancel'),
        color: 'error',
        onClick: () => {
          gaUtils.trackEvent(EventName.Click, {
            action: EventOptionClickAction.OrderCancel,
          });
          cancel(orderId);
        },
      },
      confirmCard: {
        label: t('buttons.confirm_card'),
        color: 'primary',
        disabled: isCardInfoConfirmDisabled,
        onClick: () => {
          confirmCard(paymentFormValues);
        },
      },
      confirmPayment: {
        label: t('buttons.confirm_transfer'),
        color: 'success',
        onClick: () => {
          gaUtils.trackEvent(EventName.Click, {
            action: EventOptionClickAction.ConfirmTransfer,
          });
          updatePaymentInfo(
            {
              customerConfirmStatusDetails:
                CustomerConfirmStatusDetails.CustomerPayed,
            },
            customerDataBeforePayment || isCustomerDataConfirmed
              ? {
                  onSuccess: () => {
                    if (order?.shop?.collectCustomerReceipts) {
                      setUploadReceiptModalOpen(true);
                    } else {
                      confirmPayment(orderId);
                    }
                  },
                }
              : {},
          );
        },
      },
      backToShop: {
        color: 'tertiary',
        label: t('buttons.back_to_shop'),
        onClick: () => {
          gaUtils.trackEvent(EventName.Click, {
            action: EventOptionClickAction.BackToShop,
          });
          navigateReturnUrl();
        },
        hidden:
          (status === OrderStatus.Completed && !successUrl) ||
          (status === OrderStatus.Cancelled && !failUrl) ||
          !returnUrl,
      },
      selectTradeMethod: {
        label: t('buttons.select_trade_method'),
        color: 'primary',
        onClick: () => setStartPaymentSyncFailed(false),
      },
    }),
    [
      t,
      isCardInfoConfirmDisabled,
      status,
      returnUrl,
      successUrl,
      failUrl,
      cancel,
      orderId,
      confirmCard,
      paymentFormValues,
      updatePaymentInfo,
      customerDataBeforePayment,
      isCustomerDataConfirmed,
      order?.shop?.collectCustomerReceipts,
      confirmPayment,
      navigateReturnUrl,
    ],
  );

  const isLoading = useMemo(
    () =>
      (!authenticated && isAuthLoading) ||
      !order ||
      isOrderLoading ||
      isBanksLoading ||
      isCurrenciesLoading ||
      isTradeMethodsLoading ||
      isFakeTradeMethodsLoading ||
      isReceiptsLoading,
    [
      authenticated,
      isAuthLoading,
      order,
      isOrderLoading,
      isBanksLoading,
      isCurrenciesLoading,
      isTradeMethodsLoading,
      isFakeTradeMethodsLoading,
      isReceiptsLoading,
    ],
  );

  const isTSBP = useMemo(
    () => selectedTradeMethod?.paymentType === PaymentType.TSBP,
    [selectedTradeMethod],
  );

  const isSBP = useMemo(
    () => selectedTradeMethod?.paymentType === PaymentType.SBP,
    [selectedTradeMethod],
  );

  const isSIM = useMemo(
    () => selectedTradeMethod?.paymentType === PaymentType.SIM,
    [selectedTradeMethod],
  );

  const isCard2Card = useMemo(
    () => selectedTradeMethod?.paymentType === PaymentType.Card2Card,
    [selectedTradeMethod],
  );

  const isTCard2Card = useMemo(
    () => selectedTradeMethod?.paymentType === PaymentType.TCard2Card,
    [selectedTradeMethod],
  );

  const isTSBPSingleBankSelection = useMemo(
    () =>
      isTSBP &&
      customerFields.length === 1 &&
      customerFields[0].type === FormFieldType.Bank,
    [customerFields, isTSBP],
  );

  const banksByCode = useMemo(() => keyBy(banks, (bank) => bank.code), [banks]);
  const getBankNameByCode = useCallback(
    (code?: string) => (code ? banksByCode[code]?.name || code : ''),
    [banksByCode],
  );

  const requisitesBankName = useMemo(
    () => getBankNameByCode(order?.requisites?.bank),
    [getBankNameByCode, order?.requisites?.bank],
  );

  const customerBankName = useMemo(
    () => getBankNameByCode(order?.payment?.customerBank),
    [getBankNameByCode, order?.payment?.customerBank],
  );

  const handlePaymentFormValuesChange = useCallback(
    (value: Partial<PaymentFormValues>) => {
      setPaymentFormValues((prev) => ({ ...prev, ...value }));
      if (isTSBPSingleBankSelection) {
        confirmCard(value);
      }
    },
    [confirmCard, isTSBPSingleBankSelection],
  );

  const refetchOrderStatus = useCallback(() => {
    setTimeoutRefetch(true);
  }, []);

  const contextValue = useMemo(
    (): OrderContextProps => ({
      order,
      tradeMethod: selectedTradeMethod,
      isTSBP,
      isSBP,
      isSIM,
      isTSBPSingleBankSelection,
      isCard2Card,
      isTCard2Card,
      requisitesBankName,
      qrCodeUrl,
      nspkUrl,
      customerBank: order?.payment.customerBank,
      customerBankName,
      getBankNameByCode,
    }),
    [
      customerBankName,
      isTSBP,
      isSBP,
      isSIM,
      isTSBPSingleBankSelection,
      isCard2Card,
      isTCard2Card,
      order,
      qrCodeUrl,
      nspkUrl,
      requisitesBankName,
      selectedTradeMethod,
      getBankNameByCode,
    ],
  );

  if (isLoading) {
    return <Loader />;
  }

  if (!order || authenticationFailed) {
    return <NotFoundPage />;
  }

  return (
    <OrderContext.Provider value={contextValue}>
      <Layout
        order={order}
        banks={banks}
        status={status}
        cardConfirmed={isCustomerDataConfirmed}
      >
        {status === OrderStatus.New &&
          !isStartPaymentSyncInProgress &&
          (startPaymentSyncFailed ? (
            <PaymentStatus
              order={{
                ...order,
                status: OrderStatus.Cancelled,
                statusDetails: CancelStatusDetails.CancelledRequisitesTimeout,
              }}
              actions={[actions.selectTradeMethod]}
            />
          ) : (
            <PaymentRules
              tradeMethods={
                sciDomainConfig?.shopSCITradeMethodsOnly
                  ? fakeTradeMethods
                  : tradeMethods
              }
              handleSelect={selectPaymentType}
            />
          ))}
        {(status === OrderStatus.Requisites ||
          paymentFieldsLoading ||
          isStartPaymentSyncInProgress) && (
          <PaymentLoading status={OrderStatus.Requisites} />
        )}
        {status === OrderStatus.CustomerConfirm &&
          !paymentFieldsLoading &&
          selectedTradeMethod &&
          //  TODO should show PaymentCard according to customerConfirmStatusDetails?
          (!isCustomerDataConfirmed &&
          (customerDataBeforePayment ||
            order?.statusDetails ===
              CustomerConfirmStatusDetails.CustomerPayed) ? (
            <PaymentCard
              order={order}
              banks={banks}
              tradeMethod={selectedTradeMethod}
              customerFields={customerFields}
              paymentFormValues={paymentFormValues}
              onPaymentFormValuesChange={handlePaymentFormValuesChange}
              refetchOrderStatus={refetchOrderStatus}
              actions={
                isTSBPSingleBankSelection
                  ? [actions.cancel]
                  : [actions.cancel, actions.confirmCard]
              }
            />
          ) : order?.statusDetails !==
            CustomerConfirmStatusDetails.CustomerPayed ? (
            <PaymentDetails
              order={order}
              tradeMethod={selectedTradeMethod}
              currencies={currencies}
              refetchOrderStatus={refetchOrderStatus}
              actions={[actions.cancel, actions.confirmPayment]}
            />
          ) : (
            order?.shop?.collectCustomerReceipts && (
              <Fragment>
                {!uploadReceiptModalOpen && (
                  <Button
                    className="tw-max-w-md tw-max-h-16 tw-mb-4"
                    color="success"
                    onClick={() => setUploadReceiptModalOpen(true)}
                  >
                    {tUploadReceipt('upload')}
                  </Button>
                )}
                <UploadReceiptDialog
                  open={uploadReceiptModalOpen}
                  order={order}
                  onClose={() => setUploadReceiptModalOpen(false)}
                  onUpload={() => confirmPayment(orderId)}
                  localeOverride={uploadModalLocaleOverride}
                />
              </Fragment>
            )
          ))}
        {[OrderStatus.TraderConfirm, OrderStatus.HoldCompleted].includes(
          status,
        ) && <PaymentLoading status={status} />}
        {(status === OrderStatus.Completed ||
          status === OrderStatus.Cancelled ||
          status === OrderStatus.Dispute) && (
          <PaymentStatus
            order={order}
            actions={[actions.backToShop]}
            telegramSupportBotUrl={sciDomainConfig?.telegramSupportBotUrl}
            canManageReceipts={canManageReceipts}
            receipts={receipts}
            onReceiptsUpload={refetchOrder}
          />
        )}
      </Layout>
    </OrderContext.Provider>
  );
};
